import Editor, { Monaco } from '@monaco-editor/react';
import { AlertCircle, Check } from 'lucide-react';
import type { editor } from 'monaco-editor';
import React, { useCallback, useEffect, useRef, useState } from 'react';

function collectUnrecognizedFieldMarkers(
  text: string,
  validFields: string[] | undefined,
  model: editor.ITextModel,
  markers: editor.IMarkerData[],
): void {
  if (!validFields || validFields.length === 0) {
    return;
  }

  const fieldRegex = /"field"\s*:\s*"([^"]+)"/g;
  let fieldMatch;

  while ((fieldMatch = fieldRegex.exec(text)) !== null) {
    const fieldValue = fieldMatch[1];

    if (fieldValue.startsWith('<<PLACEHOLDER_')) {
      continue;
    }

    if (!validFields.includes(fieldValue)) {
      const valueStartIndex =
        fieldMatch.index + fieldMatch[0].indexOf(`"${fieldValue}"`);
      const valueEndIndex = valueStartIndex + fieldValue.length + 2;

      const startPos = model.getPositionAt(valueStartIndex);
      const endPos = model.getPositionAt(valueEndIndex);

      markers.push({
        severity: 8, // MarkerSeverity.Error
        message: `Field "${fieldValue}" not found in available fields`,
        startLineNumber: startPos.lineNumber,
        startColumn: startPos.column,
        endLineNumber: endPos.lineNumber,
        endColumn: endPos.column,
      });
    }
  }
}

interface MonacoJsonEditorProps {
  value: string;
  onChange: (value: string) => void;
  height?: string;
  readOnly?: boolean;
  onValidationChange?: (isValid: boolean, error?: string) => void;
  highlightPlaceholders?: boolean;
  validFields?: string[];
}

export const MonacoJsonEditor: React.FC<MonacoJsonEditorProps> = ({
  value,
  onChange,
  height = '400px',
  readOnly = false,
  onValidationChange,
  highlightPlaceholders = false,
  validFields,
}) => {
  const editorRef = useRef<editor.IStandaloneCodeEditor | null>(null);
  const monacoRef = useRef<Monaco | null>(null);
  const [validationError, setValidationError] = useState<string>('');
  const [isValid, setIsValid] = useState(true);
  const [hasFieldErrors, setHasFieldErrors] = useState(false);
  const isJsonValidRef = useRef(true);

  const updateFieldMarkers = useCallback(() => {
    if (!editorRef.current || !monacoRef.current) {
      return;
    }

    const model = editorRef.current.getModel();
    if (!model) return;

    const text = model.getValue();
    const markers: editor.IMarkerData[] = [];

    // Match <<PLACEHOLDER_...>> patterns
    if (highlightPlaceholders) {
      const placeholderRegex = /<<PLACEHOLDER_[^>]+>>/g;
      let match;

      while ((match = placeholderRegex.exec(text)) !== null) {
        const startPos = model.getPositionAt(match.index);
        const endPos = model.getPositionAt(match.index + match[0].length);

        markers.push({
          severity: monacoRef.current.MarkerSeverity.Error,
          message: `Replace "${match[0]}" with an actual field name from the Available Fields list`,
          startLineNumber: startPos.lineNumber,
          startColumn: startPos.column,
          endLineNumber: endPos.lineNumber,
          endColumn: endPos.column,
        });
      }
    }

    collectUnrecognizedFieldMarkers(text, validFields, model, markers);

    monacoRef.current.editor.setModelMarkers(
      model,
      'field-validation',
      markers,
    );

    // Track if there are field validation errors
    const hasErrors = markers.length > 0;
    setHasFieldErrors(hasErrors);

    // Notify parent of combined validation state
    if (onValidationChange) {
      onValidationChange(isJsonValidRef.current && !hasErrors);
    }
  }, [highlightPlaceholders, validFields, onValidationChange]);

  useEffect(() => {
    updateFieldMarkers();
  }, [value, updateFieldMarkers]);

  const handleEditorDidMount = (
    editorInstance: editor.IStandaloneCodeEditor,
    monaco: Monaco,
  ) => {
    editorRef.current = editorInstance;
    monacoRef.current = monaco;

    updateFieldMarkers();
  };

  const validateJson = (
    jsonString: string,
  ): { valid: boolean; error?: string } => {
    if (!jsonString.trim()) {
      return { valid: false, error: 'JSON content is required' };
    }

    try {
      JSON.parse(jsonString);
      return { valid: true };
    } catch (err) {
      const error = err as Error;
      return { valid: false, error: error.message };
    }
  };

  const handleChange = (newValue: string | undefined) => {
    const val = newValue || '';
    onChange(val);

    const validation = validateJson(val);
    setIsValid(validation.valid);
    setValidationError(validation.error || '');
    isJsonValidRef.current = validation.valid;

    // Note: field marker validation will call onValidationChange after markers are updated
  };

  return (
    <div className="space-y-2">
      {!isValid && validationError && (
        <div className="bg-red-50 border border-red-200 rounded-md p-3 flex items-start">
          <AlertCircle className="h-4 w-4 text-red-600 mr-2 mt-0.5 flex-shrink-0" />
          <div className="flex-1">
            <p className="text-sm font-medium text-red-800">Invalid JSON</p>
            <p className="text-xs text-red-700 mt-1">{validationError}</p>
          </div>
        </div>
      )}

      {isValid && value && !hasFieldErrors && (
        <div className="bg-green-50 border border-green-200 rounded-md p-2 flex items-center gap-2">
          <Check className="h-4 w-4 text-green-600 flex-shrink-0" />
          <span className="text-sm text-green-800 leading-4">Valid JSON</span>
        </div>
      )}

      {isValid && value && hasFieldErrors && (
        <div className="bg-amber-50 border border-amber-200 rounded-md p-2 flex items-center gap-2">
          <AlertCircle className="h-4 w-4 text-amber-600 flex-shrink-0" />
          <span className="text-sm text-amber-800 leading-4">
            Valid JSON, but contains unrecognized field names
          </span>
        </div>
      )}

      <div className="border border-gray-300 rounded-md overflow-hidden">
        <Editor
          height={height}
          defaultLanguage="json"
          value={value}
          onChange={handleChange}
          onMount={handleEditorDidMount}
          options={{
            minimap: { enabled: false },
            formatOnPaste: true,
            formatOnType: true,
            scrollBeyondLastLine: false,
            wordWrap: 'on',
            wrappingIndent: 'deepIndent',
            automaticLayout: true,
            tabSize: 2,
            lineNumbers: 'on',
            readOnly,
            fontSize: 13,
            roundedSelection: false,
            scrollbar: {
              vertical: 'visible',
              horizontal: 'visible',
            },
            padding: {
              top: 12,
              bottom: 8,
            },
          }}
        />
      </div>
    </div>
  );
};
